import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import { useI18n } from '../contexts/I18nContext';
import '../styles/components/CodeEditorModal.css';

interface MarkdownEditorModalProps {
  isOpen: boolean;
  markdown: string;
  onSave: (markdown: string) => void;
  onClose: () => void;
}

const MarkdownEditorModal: React.FC<MarkdownEditorModalProps> = ({
  isOpen,
  markdown,
  onSave,
  onClose,
}) => {
  const { t } = useI18n();
  const [editMarkdown, setEditMarkdown] = useState(markdown);
  const [viewMode, setViewMode] = useState<'edit' | 'preview' | 'split'>('split');
  const editorRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditMarkdown(markdown);
  }, [markdown, isOpen]);

  useEffect(() => {
    if (isOpen && editorRef.current) {
      editorRef.current.focus();
    }
  }, [isOpen]);

  const handleSave = () => {
    onSave(editMarkdown);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      setEditMarkdown(prev => prev + '\n');
    }
  };

  const insertMarkdown = (before: string, after: string = '') => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = editMarkdown.substring(start, end);
    const newText = editMarkdown.substring(0, start) + before + selectedText + after + editMarkdown.substring(end);

    setEditMarkdown(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  if (!isOpen) return null;

  return (
    <div className="code-editor-modal-overlay" onClick={onClose}>
      <div
        className="code-editor-modal-content"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        style={{ maxWidth: '90vw', maxHeight: '90vh', width: '1200px', height: '700px' }}
      >
        <div className="code-editor-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              className={viewMode === 'edit' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setViewMode('edit')}
              style={{ padding: '6px 12px', fontSize: '13px' }}
            >
              {t.modals.markdownEditor.edit}
            </button>
            <button
              className={viewMode === 'split' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setViewMode('split')}
              style={{ padding: '6px 12px', fontSize: '13px' }}
            >
              {t.modals.markdownEditor.split}
            </button>
            <button
              className={viewMode === 'preview' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setViewMode('preview')}
              style={{ padding: '6px 12px', fontSize: '13px' }}
            >
              {t.modals.markdownEditor.preview}
            </button>
          </div>
          <div className="code-editor-modal-actions">
            <button onClick={onClose} className="btn-secondary">
              {t.modals.markdownEditor.cancel}
            </button>
            <button onClick={handleSave} className="btn-primary">
              {t.modals.markdownEditor.save}
            </button>
          </div>
        </div>

        <div className="code-editor-modal-body" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' }}>
          {/* 툴바 */}
          <div style={{
            padding: '8px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap'
          }}>
            <button onClick={() => insertMarkdown('**', '**')} style={{ padding: '4px 8px', fontSize: '12px' }} title={t.modals.markdownEditor.bold}>B</button>
            <button onClick={() => insertMarkdown('*', '*')} style={{ padding: '4px 8px', fontSize: '12px', fontStyle: 'italic' }} title={t.modals.markdownEditor.italic}>I</button>
            <button onClick={() => insertMarkdown('`', '`')} style={{ padding: '4px 8px', fontSize: '12px' }} title={t.modals.markdownEditor.code}>`</button>
            <div style={{ width: '1px', background: '#e5e7eb', margin: '0 4px' }}></div>
            <button onClick={() => insertMarkdown('# ')} style={{ padding: '4px 8px', fontSize: '12px' }}>H1</button>
            <button onClick={() => insertMarkdown('## ')} style={{ padding: '4px 8px', fontSize: '12px' }}>H2</button>
            <button onClick={() => insertMarkdown('### ')} style={{ padding: '4px 8px', fontSize: '12px' }}>H3</button>
            <div style={{ width: '1px', background: '#e5e7eb', margin: '0 4px' }}></div>
            <button onClick={() => insertMarkdown('- ')} style={{ padding: '4px 8px', fontSize: '12px' }}>-</button>
            <button onClick={() => insertMarkdown('1. ')} style={{ padding: '4px 8px', fontSize: '12px' }}>1.</button>
            <button onClick={() => insertMarkdown('> ')} style={{ padding: '4px 8px', fontSize: '12px' }}>{'>'}</button>
            <button onClick={() => insertMarkdown('```\n', '\n```')} style={{ padding: '4px 8px', fontSize: '12px' }}>{'```'}</button>
          </div>

          {/* 편집 영역 */}
          <div style={{
            display: viewMode === 'edit' ? 'flex' : viewMode === 'preview' ? 'flex' : 'grid',
            gridTemplateColumns: viewMode === 'split' ? '1fr 1fr' : 'none',
            height: '100%',
            overflow: 'hidden'
          }}>
            {(viewMode === 'edit' || viewMode === 'split') && (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                borderRight: viewMode === 'split' ? '1px solid #e5e7eb' : 'none'
              }}>
                <textarea
                  ref={editorRef}
                  value={editMarkdown}
                  onChange={(e) => setEditMarkdown(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '16px',
                    border: 'none',
                    outline: 'none',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    resize: 'none',
                    background: '#f9fafb'
                  }}
                  placeholder={t.modals.markdownEditor.placeholder}
                />
              </div>
            )}

            {(viewMode === 'preview' || viewMode === 'split') && (
              <div style={{
                flex: 1,
                padding: '16px',
                overflow: 'auto',
                background: 'white'
              }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                >
                  {editMarkdown || t.modals.markdownEditor.previewHint}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkdownEditorModal;
